import { formatPrice } from '@ynap/intl-formatters'
import { pdpSelectors } from '@ynap/product-details-state'
import getOrderStatus from './getOrderStatus'
import { productSelectors } from '@ynap/product-state'
import { mapOrderStatus } from '../../../utils/order-status/map-order-status'

const getProductColours = ({ productColours }, selectedPartNumber, exchangeSkus) => {
    if (productColours.length === 1) {
        return [{ ...productColours[0], selected: true }]
    } else {
        return productColours.reduce((colours, colour) => {
            const { visible = true } = colour
            if (!visible) return colours

            const { partNumber, selected: selectedFromResponse, sKUs } = colour
            const selected = selectedPartNumber ? partNumber === selectedPartNumber : selectedFromResponse
            const sKUsPartNumbers = sKUs.map((obj) => obj.partNumber)
            let intersection = []

            if (selected) {
                return [...colours, { ...colour, selected }]
            }

            if (exchangeSkus.length) {
                intersection = sKUsPartNumbers.filter((x) => exchangeSkus.includes(x))
            }

            if (intersection.length) {
                return [...colours, { ...colour, selected }]
            } else {
                return [...colours]
            }
        }, [])
    }
}

export const getSelectedProductColour = (productColours, partNumber) => {
    return productColours?.find((colour) => {
        const { sKUs } = colour
        return !!sKUs.find((sku) => sku.partNumber === partNumber)
    })
}

export const getSelectedProductColourSwatch = (productColours, partNumber) => {
    return productColours?.find((colour) => colour.partNumber === partNumber)
}

const getSize = (sKUs, partNumber) => {
    const sku = sKUs.find((element) => {
        return element.partNumber === partNumber
    })
    return sku?.size
}

const { getProductLinkByPartNumber, getAttribute } = productSelectors
const { getColourSwatches, getSelectedProductImages } = pdpSelectors

export default function getOrderItems(state, props, isLegacyOrder) {
    const { messages } = props
    const { orderDetails, locale, config } = state
    const { orderItem, isReturnable: orderReturnable } = orderDetails
    const { mainImageShot, flags, availableImageSizes, aspectRatio } = config.head

    // TON do not want this, hence toggle
    // to be removed when backend work is done to fix this in WCS
    const { showOnlyFinalPrice } = flags
    const isOnlyFinalPrice = !!showOnlyFinalPrice
    const imagesphereAspectRatio = aspectRatio?.imagesphere || ''

    return isLegacyOrder === 'true'
        ? orderItem?.map((item) => {
              const { catalogEntryDetails, orderItemPrice } = item
              const {
                  name,
                  nameEN,
                  designerName,
                  designerNameEN,
                  designerIdentifier,
                  partNumber,
                  masterCategory,
                  tracking,
                  productColours,
                  legacyImageURL,
              } = catalogEntryDetails

              const base = productColours?.[0]
              const colour = base && getSelectedProductColour(productColours, item.partNumber)
              const size = colour && getSize(colour.sKUs, item.partNumber)
              const { status, statusEN } = getOrderStatus(item.orderItemStatus, messages)
              const images = colour
                  ? getSelectedProductImages(
                        catalogEntryDetails,
                        colour,
                        availableImageSizes,
                        undefined,
                        null,
                        imagesphereAspectRatio
                    )
                  : [{ url: legacyImageURL, id: catalogEntryDetails.partNumber }]

              return {
                  analytics: {
                      name: tracking?.name || nameEN || name,
                      brand: designerNameEN,
                      brandId: designerIdentifier,
                      productID: partNumber,
                      masterCategory,
                  },
                  partNumber: partNumber,
                  image: images[0],
                  images: images,
                  colour: colour?.label || '',
                  size: size?.centralSizeLabel || size?.labelSize || '',
                  brand: designerName,
                  name: name,
                  orderItemStatus: status,
                  orderItemStatusEN: statusEN,
                  primeLineNo: item.primeLineNo,
                  quantity: item.quantity,
                  price: {
                      originalPrice: undefined,
                      markedDownPercentDiscount: undefined,
                      finalPrice: formatPrice(
                          orderItemPrice.value.amount / orderItemPrice.value.divisor,
                          orderItemPrice.currency.label,
                          locale.id
                      ),
                      currency: orderItemPrice.currency,
                  },
              }
          })
        : orderItem?.map((item) => {
              const {
                  unitPrice,
                  orderItemPrice,
                  orderItemPriceWithoutTax,
                  listPrice,
                  markDownPercent,
                  catalogEntryDetails,
                  returnCodes,
                  selectedReturnCode,
                  selectedProductColour,
                  isReturnable,
                  returnOrExchange,
                  shippingMethodName,
              } = item

              const {
                  name,
                  nameEN,
                  designerName,
                  designerNameEN,
                  designerIdentifier,
                  badges,
                  masterCategory,
                  tracking,
                  legacyImageURL,
                  attributes,
              } = catalogEntryDetails

              const selectedCode = returnCodes?.find((returnCode) => returnCode.code === selectedReturnCode)
              const exchangeSkus = selectedCode?.availableExchanges || []

              const showCommentsBox = selectedCode?.attributes?.find(
                  (attr) => attr.name === 'comments' && attr.value === 'true'
              )
              const base = catalogEntryDetails?.productColours?.[0]

              const productColours = base && getProductColours(catalogEntryDetails, selectedProductColour, exchangeSkus)
              const colour = base && getSelectedProductColour(catalogEntryDetails.productColours, item.partNumber)

              const partNumber = colour?.partNumber
              const finalSale = colour?.sKUs.find((sku) => sku.partNumber === item.partNumber)?.finalSale

              const selectedProductColourSwatch =
                  productColours && getSelectedProductColourSwatch(productColours, selectedProductColour)

              const initialSelectedProductColourSwatch =
                  productColours && getSelectedProductColour(productColours, item.partNumber)

              const selectedProductColourVariant =
                  selectedProductColourSwatch?.partNumber || initialSelectedProductColourSwatch?.partNumber

              const size = colour && getSize(colour.sKUs, item.partNumber)

              const images = colour
                  ? getSelectedProductImages(
                        catalogEntryDetails,
                        colour,
                        availableImageSizes,
                        undefined,
                        null,
                        imagesphereAspectRatio
                    )
                  : [{ url: legacyImageURL, id: catalogEntryDetails.partNumber }]

              const selectedProductName =
                  selectedProductColourSwatch?.shortDescription ||
                  initialSelectedProductColourSwatch?.shortDescription ||
                  name

              const soldOut = badges?.find((badge) => {
                  return badge.key === 'SOLD_OUT'
              })

              const exchanges =
                  returnCodes?.filter((code) => {
                      if (code?.availableExchanges?.length) {
                          return code.attributes.find((attribute) => {
                              return attribute.name === 'exchangeOnly' && attribute.value !== 'NONE'
                          })
                      }
                  }) || []

              const returnExchange =
                  soldOut || exchanges.length <= 0 ? 'return' : returnOrExchange ? returnOrExchange : 'exchange'

              const productUrlTemplate = config?.global?.urlConfig?.pdp?.construct || ''
              const productURL = getProductLinkByPartNumber(productUrlTemplate, locale.id, colour?.seo, item.partNumber)

              let finalPrice = formatPrice(
                  orderItemPrice.value.amount / orderItemPrice.value.divisor,
                  orderItemPrice.currency.label,
                  locale.id
              )

              const unitPriceValue = isOnlyFinalPrice
                  ? null
                  : formatPrice(unitPrice.value.amount / unitPrice.value.divisor, unitPrice.currency.label, locale.id)

              const isMarkedDown = !!Number(markDownPercent)
              const hasPromotion = unitPriceValue !== finalPrice

              let originalPrice =
                  (isMarkedDown || hasPromotion) &&
                  listPrice?.value?.amount &&
                  listPrice.value.amount !== '' &&
                  formatPrice(listPrice.value.amount / listPrice.value.divisor, listPrice.currency.label, locale.id)

              const itemStatus = item?.orderItemStatus?.toLowerCase() || ''

              // if order is cancelled, as TON do not get orderItemPrice back only display markedDown
              if (originalPrice && itemStatus === 'cancelled') {
                  finalPrice = null
                  originalPrice = null
              } else if (isOnlyFinalPrice) {
                  originalPrice = null
              }

              const isCancelled = itemStatus.includes('cancelled') || false
              const isReturn = itemStatus.includes('return') || false
              const isExchange = itemStatus.includes('exchange') || false
              const isReturnRefused = itemStatus.includes('refused') || itemStatus.includes('reshipped') || false
              const { status, statusEN } = getOrderStatus(item.orderItemStatus, messages)

              return {
                  partNumber: item.partNumber,
                  image: images[0],
                  images: images,
                  brand: designerName,
                  consideredBadge: !!getAttribute(attributes, 'CONSIDERED'),
                  analytics: {
                      name: tracking?.name || nameEN || name,
                      brand: designerNameEN,
                      brandId: designerIdentifier,
                      productID: partNumber,
                      masterCategory,
                  },
                  name: selectedProductName,
                  colour: colour?.label || '',
                  size: size?.centralSizeLabel || size?.labelSize || '',
                  orderItemStatus: status,
                  orderItemStatusEN: statusEN,
                  showOrderItemStatus: isCancelled || isReturn || isExchange,
                  primeLineNo: item.primeLineNo,
                  soldOut: soldOut,
                  quantity: item.quantity,
                  price: {
                      originalPrice,
                      adjustedOrderItemPrice: true,
                      markedDownPercentDiscount: !isOnlyFinalPrice && isMarkedDown && Math.round(markDownPercent),
                      finalPrice,
                      markedDownPrice: isMarkedDown && hasPromotion && unitPriceValue,
                      currency: orderItemPrice.currency,
                      priceReduced: !isOnlyFinalPrice && !!markDownPercent,
                      orderItemPrice: finalPrice,
                      value: orderItemPrice.value,
                  },
                  colours: catalogEntryDetails && base && getColourSwatches(productColours, undefined, locale.id),
                  coloursWithSkus: productColours,
                  skus: colour?.sKUs,
                  selectedProductColour: selectedProductColourVariant,
                  showCommentsBox,
                  selectedReturnCode,
                  returnOrExchange: returnExchange,
                  returnCodes: returnExchange === 'exchange' && exchanges ? exchanges : item.returnCodes,
                  isItemReturnable: orderReturnable === 'true' && isReturnable === 'true',
                  exchangable: exchanges && exchanges.length > 0 ? true : false,
                  productURL,
                  finalSale,
                  extnReturnTrackingNo: item.extnReturnTrackingNo,
                  isReturn,
                  isReturnRefused,
                  activeOrderItemStatus: mapOrderStatus(item?.orderItemStatus),
                  orderItemPriceWithoutTax:
                      orderItemPriceWithoutTax.value.amount / orderItemPriceWithoutTax.value.divisor,
                  shippingMethodName,
              }
          })
}
